/**
 * SPRINT 4J: Multi-Vendor Cart Page
 * Display cart items grouped by vendor with delivery fee calculation
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, AlertTriangle, MapPin, Package } from 'lucide-react';
import { useCartStore } from '@/store/cart';
import { QuantitySelector } from '@/components/cart/WeightSelector';

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, isLoading, fetchCart, updateItem, removeItem, clearCart } = useCartStore();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (isLoading && !cart) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cart...</p>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <ShoppingCart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">
            Start shopping to add items to your cart
          </p>
          <button
            onClick={() => navigate('/search')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">My Cart</h1>
            {cart.items.length > 0 && (
              <button
                onClick={clearCart}
                className="text-red-600 hover:text-red-700 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm font-medium">Clear Cart</span>
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {cart.total_items} item{cart.total_items !== 1 ? 's' : ''} from{' '}
            {cart.vendor_totals.length} vendor{cart.vendor_totals.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Cross-Zone Warning */}
            {cart.has_cross_zone_items && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-yellow-900 mb-1">
                    Items from multiple zones detected
                  </h3>
                  <p className="text-sm text-yellow-700">
                    You can only order from one zone at a time. Please remove items from other zones
                    before checkout.
                  </p>
                </div>
              </div>
            )}

            {/* Vendor Sections */}
            {cart.vendor_totals.map((vendorTotal) => (
              <div key={vendorTotal.vendor_id} className="bg-white rounded-lg border overflow-hidden">
                {/* Vendor Header */}
                <div className="bg-gray-50 border-b px-4 py-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">{vendorTotal.vendor_name}</h3>
                    <span className="text-sm text-gray-600">
                      {vendorTotal.item_count} item{vendorTotal.item_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Vendor Items */}
                <div className="divide-y">
                  {vendorTotal.items.map((item) => (
                    <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex-shrink-0">
                          {item.product_image_url && (
                            <img
                              src={item.product_image_url}
                              alt={item.product_name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 mb-1">{item.product_name}</h4>
                          {item.product_category && (
                            <p className="text-xs text-gray-500 mb-2">{item.product_category}</p>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <span className="text-gray-600">
                                ₱{item.locked_gross_price.toFixed(2)}
                              </span>
                              {item.weight_kg && (
                                <span className="text-gray-500"> / kg</span>
                              )}
                            </div>
                            <span className="font-bold text-green-600">
                              ₱{item.subtotal.toFixed(2)}
                            </span>
                          </div>

                          {/* Quantity/Weight Selector */}
                          <div className="mt-3 flex items-center justify-between">
                            {item.weight_kg ? (
                              <div className="text-sm">
                                <span className="font-medium">{item.quantity} kg</span>
                              </div>
                            ) : (
                              <QuantitySelector
                                quantity={item.quantity}
                                onChange={(qty) => updateItem(item.id, qty)}
                              />
                            )}

                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-red-600 hover:text-red-700 p-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Vendor Subtotal */}
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Vendor Subtotal:</span>
                    <span className="font-bold text-lg">₱{vendorTotal.subtotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1 mt-4 lg:mt-0">
            <div className="bg-white rounded-lg border p-6 sticky top-20">
              <h3 className="font-bold text-lg mb-4">Order Summary</h3>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₱{cart.subtotal.toFixed(2)}</span>
                </div>

                {/* Delivery Fee Breakdown */}
                {cart.delivery_fee_breakdown ? (
                  <>
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          Base Fee:
                        </span>
                        <span>₱{cart.delivery_fee_breakdown.base_fee.toFixed(2)}</span>
                      </div>
                      {cart.delivery_fee_breakdown.extra_km_fee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            Extra Distance ({cart.delivery_fee_breakdown.extra_km}km):
                          </span>
                          <span>₱{cart.delivery_fee_breakdown.extra_km_fee.toFixed(2)}</span>
                        </div>
                      )}
                      {cart.delivery_fee_breakdown.extra_weight_fee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Extra Weight ({cart.delivery_fee_breakdown.extra_weight}kg):
                          </span>
                          <span>₱{cart.delivery_fee_breakdown.extra_weight_fee.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-medium">Delivery Fee:</span>
                      <span className="font-bold text-green-600">
                        ₱{cart.delivery_fee_breakdown.total_fee.toFixed(2)}
                      </span>
                    </div>

                    {/* Weight Info */}
                    <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                      <p>Distance: {cart.delivery_fee_breakdown.distance_km} km</p>
                      <p>Total Weight: {cart.total_weight_kg} kg</p>
                    </div>
                  </>
                ) : (
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-medium">Delivery Fee:</span>
                    <span className="text-sm text-gray-500">Calculated at checkout</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Total:</span>
                  <span className="font-bold text-2xl text-green-600">
                    ₱{cart.grand_total.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={cart.has_cross_zone_items}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {cart.has_cross_zone_items ? 'Fix Zone Issues' : 'Proceed to Checkout'}
              </button>

              {cart.has_cross_zone_items && (
                <p className="text-xs text-red-600 text-center mt-2">
                  Remove items from other zones to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
